/* eslint-disable jsx-a11y/mouse-events-have-key-events */
import React, { useCallback, memo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { push } from 'connected-react-router';
import { includes, isString } from 'lodash';
import { dotmapsGrayButtonBackground, overlayTextColor } from '@constants/colors';
import { tableFloatingBarStyles } from '@constants/mui-theme';
import TruncatedCell from '@shared/data-table/cells/truncated-cell';
import { getFieldInfo, isRowEditable } from '@utils/data-table-utils';
import { getCurrentEntityTypes } from '@selectors/entities-selector';
import './body.scss';

const Body = ({ columns, dataType, isFixed, rows, subType }) => {
  const dispatch = useDispatch();
  const selectedRow = useSelector(state => state.dataTables.selectedRow);
  const entityList = useSelector(state => Object.keys(getCurrentEntityTypes(state)));

  // Return true if the row can be edited.
  const isEditable = useCallback(row => isRowEditable(dataType, subType, row), [dataType, subType]);

  // Returns true if the data type is an entity,
  // since entities have enabled some additional functionality).
  const isEntity = useCallback(() => includes(entityList, dataType), [entityList, dataType]);

  // Returns the CSS style for the row, which is a
  // pointer cursor, if the row is editable.
  const getRowStyle = useCallback(row => {
    const rowStyle = {};
    if (isEditable(row)) {
      rowStyle.cursor = 'pointer';
    }
    if (selectedRow === row.id) {
      rowStyle.backgroundColor = dotmapsGrayButtonBackground;
    }
    return rowStyle;
  }, [isEditable, selectedRow]);

  const onRowClick = useCallback((event, row) => {
    const { id } = row;

    if (!isEditable(row)) {
      return;
    }

    let url = `/${dataType}/${id}`;
    if (subType) {
      url = `/${dataType}/${subType}/${id}`;
    }
    if (dataType === 'activity') {
      const { cycle_id, task } = row;
      url = `/cycle/${cycle_id}/task/${task}/activity/${id}`;
    }
    if (dataType === 'task') {
      const { cycle_id } = row;
      url = `/cycle/${cycle_id}/task/${id}`;
    }
    // Simulate CTRL or SHIFT CLICK behavior. It might not work on all browsers.
    if (event.ctrlKey) {
      // Simulate CTRL-CLICK behavior on links (open link in a new tab):
      // (works on Chrome and Firefox).
      const win = window.open(url);
      win.focus();
    } else if (event.shiftKey) {
      // Simulate SHIFT-CLICK behavior on links (open link in a new window):
      // (works on Chrome, but on Firefox, which opens it on a new tab).
      window.open(url);
    } else {
      // Else, go to the specified link in the current browser window.
      const source = location.pathname || `/library/entity/${dataType}`;
      dispatch(push({ pathname: url, state: { clear: true, source } }));
    }
  }, [dataType, subType, isEditable, dispatch]);

  const getColumnWidth = useCallback(width => ({
    maxWidth: `${width}rem`,
    minWidth: `${width}rem`,
    width: `${width}rem`
  }), []);

  const getRowId = useCallback(event => event.currentTarget.id.split('_').pop(), []);

  const getRows = useCallback(event => {
    const id = getRowId(event);
    const fixed = document.getElementById(`row_fixed_${id}`);
    const scroll = document.getElementById(`row_scroll_${id}`);
    return { fixed, scroll };
  }, [getRowId]);

  const getColumn = useCallback(event => {
    const id = getRowId(event);
    return document.getElementById(`column_${id}`);
  }, [getRowId]);

  // hoverOn()/hoverOff() will always work correctly and make the
  // 'action' column appear and disappear.
  //
  // However we want that column to still be enabled when we click on
  // the 'more_vert' context menu, and hoverOff() is always triggered
  // in that case.
  //
  // To do that, we set the 'selectedRow' flag on the Redux store,
  // which works, but it keeps it open forever until we 'hover' that
  // row again.
  //
  // Thus we need to store the last selected row id in 'window.hoveredColumnId'
  // so we can clear it as soon as we 'hover' any column (usually after cancelling
  // the dialog about adding an entity to a group).
  const clearLastHoveredColumn = useCallback(event => {
    const id = getRowId(event);
    if (window.hoveredColumnId) {
      if (window.hoveredColumnId !== id) {
        const column = document.getElementById(`column_${window.hoveredColumnId}`);
        if (column) {
          column.style.opacity = 0;
        }
      }
    }
    window.hoveredColumnId = id;
  }, [getRowId]);

  const hoverOn = useCallback(event => {
    const rowElements = getRows(event);
    rowElements.fixed.style.backgroundColor = dotmapsGrayButtonBackground;
    rowElements.scroll.style.backgroundColor = dotmapsGrayButtonBackground;
    if (isEntity()) {
      const column = getColumn(event);
      if (column) {
        column.style.opacity = 1;
        // Clear any previously selected row:
        clearLastHoveredColumn(event);
      }
    }
  }, [getRows, getColumn, isEntity, clearLastHoveredColumn]);

  const hoverOff = useCallback(event => {
    if (selectedRow) {
      // If there's a selected row, disable hoverOff() until
      // the row is unselected.
      return;
    }
    const rowElements = getRows(event);
    rowElements.fixed.style.backgroundColor = overlayTextColor;
    rowElements.scroll.style.backgroundColor = overlayTextColor;
    if (isEntity()) {
      const column = getColumn(event);
      if (column) {
        column.style.opacity = 0;
      }
    }
  }, [selectedRow, getRows, getColumn, isEntity]);

  const renderColumn = useCallback((key, row) => {
    const columnData = getFieldInfo(key, row);
    // Truncate all text columns if they overflow:
    if (isString(columnData)) {
      return <TruncatedCell>{columnData}</TruncatedCell>;
    }
    return columnData;
  }, []);

  return (
    <tbody data-testid="datatable-body" styleName="data-table-body-container">
      {rows.map(row => (
        <tr
          key={row.id}
          id={`row_${isFixed ? 'fixed' : 'scroll'}_${row.id}`}
          onClick={event => onRowClick(event, row)}
          onMouseOver={hoverOn}
          onMouseOut={hoverOff}
          role="listbox"
          style={getRowStyle(row)}
          styleName="data-table-body-row"
        >
          {columns.map(({ key, styleName, width }) => (
            <td
              key={key}
              role="listbox"
              styleName={`data-table-body-column ${styleName ? styleName : ''} ${isFixed ? 'data-table-body-column-fixed' : ''}`}
              style={getColumnWidth(width)}
              tabIndex="-1"
            >
              <div styleName="column-wrapper">{renderColumn(key, row)}</div>
            </td>
          ))}
          {!isFixed && isEntity() && (
            <td
              id={`column_${row.id}`}
              style={tableFloatingBarStyles.wrapper}
            >
              <div style={tableFloatingBarStyles.bar}>
                {getFieldInfo('action', row)}
              </div>
            </td>
          )}
        </tr>
      ))}
    </tbody>
  );
};

Body.propTypes = {
  columns: PropTypes.array,
  dataType: PropTypes.string,
  isFixed: PropTypes.bool,
  rows: PropTypes.array,
  subType: PropTypes.string
};

export default memo(Body);
